import React from 'react';
import { ChefHat, Wine, Users, Sparkles } from 'lucide-react';
import { aboutContent } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { FadeIn, SlideIn, StaggerContainer } from './AnimatedComponents';

const iconMap = {
  ChefHat,
  Wine,
  Users,
  Sparkles
};

const About = () => {
  const { t } = useLanguage();
  
  const features = [
    { icon: 'ChefHat', titleKey: 'features.cuisine' },
    { icon: 'Wine', titleKey: 'features.wine' },
    { icon: 'Users', titleKey: 'features.family' },
    { icon: 'Sparkles', titleKey: 'features.terrace' }
  ];

  return (
    <section id="about" className="py-24 bg-stone-950 relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-700/50 to-transparent" />
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-96 h-96 border border-amber-600 rounded-full animate-rotate-slow" />
        <div className="absolute bottom-20 right-20 w-64 h-64 border border-amber-600 rounded-full animate-rotate-slow" style={{ animationDirection: 'reverse' }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <FadeIn className="text-center mb-16">
          <p className="text-amber-500 uppercase tracking-[0.3em] text-sm mb-4">
            {t('about.subtitle')}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white">
            {t('about.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-6" />
        </FadeIn>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          {/* Image */}
          <SlideIn from="left">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-tr from-amber-600/20 to-amber-400/10 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 group-hover:from-amber-600/30" />
              <div className="relative overflow-hidden rounded-2xl img-zoom">
                <img
                  src={aboutContent.image}
                  alt="Trattoria Pizzeria Da Daniele - ristorante italiano nel cuore di Solothurn dal 2003"
                  className="w-full h-[500px] object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950/60 to-transparent" />
                
                {/* Floating Badge */}
                <div className="absolute bottom-6 left-6 bg-stone-950/80 backdrop-blur-sm px-6 py-4 rounded-xl border border-amber-700/30 hover-lift">
                  <p className="text-amber-400 text-sm uppercase tracking-wider">{t('about.since')}</p>
                  <p className="text-white text-3xl font-serif font-bold">2003</p>
                </div>
              </div>
            </div>
          </SlideIn>

          {/* Text Content */}
          <SlideIn from="right">
            <div className="space-y-6">
              {t('about.paragraphs').map((paragraph, index) => (
                <p
                  key={index}
                  className="text-amber-100/70 text-lg leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
              
              {/* Signature */}
              <div className="pt-6">
                <p className="text-amber-400 font-serif text-2xl italic">{t('about.signature')}</p>
              </div>
            </div>
          </SlideIn>
        </div>

        {/* Features Grid */}
        <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" staggerDelay={150}>
          {features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            const featureData = t(feature.titleKey);
            return (
              <div
                key={index}
                className="group bg-stone-900/50 backdrop-blur-sm border border-stone-800 hover:border-amber-700/50 rounded-2xl p-6 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-amber-900/20"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                </div>
                <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-amber-200 transition-colors">
                  {featureData?.title}
                </h3>
                <p className="text-amber-100/60 text-sm leading-relaxed">
                  {featureData?.description}
                </p>
              </div>
            );
          })}
        </StaggerContainer>
      </div>
    </section>
  );
};

export default About;
